import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Building2, Church, HandHeart, MapPin, Plus, Search, Sparkles, Globe } from "lucide-react";

const KINDS = [
  { slug: "church", label: "Churches", Icon: Church, color: "from-burgundy to-jjblue", desc: "Find a local body. Service times. Service hearts." },
  { slug: "business", label: "Businesses", Icon: Building2, color: "from-jjblue to-burgundy", desc: "Kingdom-minded businesses run by family." },
  { slug: "ministry", label: "Ministries", Icon: HandHeart, color: "from-burgundy to-gold", desc: "Nonprofits, missions, and outreaches doing the work." },
];

function Card({ listing }) {
  const KindIcon = KINDS.find((k) => k.slug === listing.kind)?.Icon || Sparkles;
  return (
    <Link
      to={`/directory/${listing.id}`}
      className="jj-card p-5 hover:border-gold transition-all duration-300 hover:-translate-y-0.5 flex flex-col"
      data-testid={`dir-card-${listing.id}`}
    >
      <div className="flex items-start gap-3">
        <div className="h-11 w-11 rounded-xl bg-burgundy/10 text-burgundy flex items-center justify-center shrink-0">
          <KindIcon className="h-5 w-5" strokeWidth={1.6} />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-serif text-xl text-jjblue leading-tight">{listing.name}</h3>
          <div className="mt-1 text-[10px] uppercase tracking-wider text-[var(--jj-muted)] flex items-center gap-2 flex-wrap">
            <span className="capitalize">{listing.kind}</span>
            {(listing.city || listing.state) && (
              <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {[listing.city, listing.state].filter(Boolean).join(", ")}</span>
            )}
            {listing.featured && <span className="px-1.5 py-0.5 rounded-full bg-gold/20 text-burgundy">Featured</span>}
          </div>
        </div>
      </div>
      {listing.description && (
        <p className="mt-3 text-sm text-[var(--jj-text-secondary)] line-clamp-3">{listing.description}</p>
      )}
      {listing.tags?.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {listing.tags.slice(0, 4).map((t) => (
            <span key={t} className="text-[10px] px-2 py-0.5 rounded-full bg-cream-surface border border-jj text-jjblue/80">#{t}</span>
          ))}
        </div>
      )}
    </Link>
  );
}

export default function Directory() {
  const { user } = useAuth();
  const [kind, setKind] = useState("all");
  const [q, setQ] = useState("");
  const [city, setCity] = useState("");
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({ church: 0, business: 0, ministry: 0 });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = { limit: 60 };
      if (kind !== "all") params.kind = kind;
      if (q.trim()) params.q = q.trim();
      if (city.trim()) params.city = city.trim();
      const { data } = await api.get("/directory", { params });
      setItems(data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { api.get("/directory/summary").then(({ data }) => setSummary(data)).catch(() => {}); }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [kind]);

  const onSearch = (e) => {
    e?.preventDefault();
    load();
  };

  const totalCount = useMemo(() => Object.values(summary).reduce((a, b) => a + b, 0), [summary]);

  return (
    <div className="min-h-screen bg-cream">
      {/* Hero */}
      <section className="bg-gradient-to-br from-jjblue via-jjblue to-burgundy text-cream">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-16 sm:py-20">
          <div className="overline text-gold flex items-center gap-2">
            <Sparkles className="h-3.5 w-3.5" /> Kingdom Directory
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-3 leading-[1.05] max-w-3xl">
            Churches, businesses, and ministries — the family, mapped.
          </h1>
          <p className="mt-5 text-base sm:text-lg text-cream/80 max-w-2xl">
            A trustworthy directory of Kingdom-minded places to worship, buy, and serve. Submitted by members, reviewed by humans.
          </p>

          <form onSubmit={onSearch} className="mt-8 flex flex-wrap gap-3 max-w-3xl">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-cream/60" />
              <input
                className="w-full bg-cream/10 border border-cream/20 rounded-full pl-10 pr-4 py-2.5 text-sm text-cream placeholder:text-cream/50 focus:outline-none focus:border-gold"
                placeholder="Search by name, tag, or description…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                data-testid="dir-search-q"
              />
            </div>
            <input
              className="bg-cream/10 border border-cream/20 rounded-full px-4 py-2.5 text-sm text-cream placeholder:text-cream/50 focus:outline-none focus:border-gold w-40"
              placeholder="City"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              data-testid="dir-search-city"
            />
            <button type="submit" className="btn-gold !py-2.5 !px-5 text-sm" data-testid="dir-search-submit">Search</button>
            {user && (
              <Link to="/directory/submit" className="btn-gold !py-2.5 !px-5 text-sm flex items-center gap-1.5" data-testid="dir-submit-btn">
                <Plus className="h-4 w-4" /> Submit listing
              </Link>
            )}
          </form>

          <div className="mt-8 flex items-center gap-2 flex-wrap text-sm">
            <button
              onClick={() => setKind("all")}
              className={`px-3 py-1.5 rounded-full transition-colors ${kind === "all" ? "bg-cream text-jjblue" : "bg-cream/10 text-cream hover:bg-cream/20"}`}
              data-testid="dir-kind-all"
            >
              All <span className="opacity-60 ml-1">{totalCount}</span>
            </button>
            {KINDS.map((k) => (
              <button
                key={k.slug}
                onClick={() => setKind(k.slug)}
                className={`px-3 py-1.5 rounded-full transition-colors ${kind === k.slug ? "bg-cream text-jjblue" : "bg-cream/10 text-cream hover:bg-cream/20"}`}
                data-testid={`dir-kind-${k.slug}`}
              >
                {k.label} <span className="opacity-60 ml-1">{summary[k.slug] || 0}</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Kind highlight cards (only on "all" view) */}
      {kind === "all" && !q && !city && (
        <section className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 -mt-8">
          <div className="grid sm:grid-cols-3 gap-4">
            {KINDS.map((k) => (
              <button
                key={k.slug}
                onClick={() => setKind(k.slug)}
                className={`relative text-left overflow-hidden rounded-2xl p-6 text-cream bg-gradient-to-br ${k.color} shadow-lg hover:scale-[1.02] transition-transform`}
                data-testid={`dir-feature-${k.slug}`}
              >
                <k.Icon className="h-8 w-8 text-gold" strokeWidth={1.4} />
                <h2 className="font-serif text-2xl mt-4">{k.label}</h2>
                <p className="mt-1.5 text-sm text-cream/80">{k.desc}</p>
                <div className="mt-4 text-xs uppercase tracking-wider text-cream/70">{summary[k.slug] || 0} listed</div>
              </button>
            ))}
          </div>
        </section>
      )}

      {/* Results */}
      <section className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-12">
        {loading ? (
          <div className="py-16 text-center text-[var(--jj-muted)]">Loading…</div>
        ) : items.length === 0 ? (
          <div className="jj-card p-10 text-center">
            <Globe className="h-10 w-10 text-burgundy/40 mx-auto" strokeWidth={1.3} />
            <h3 className="font-serif text-2xl text-jjblue mt-4">No listings yet.</h3>
            <p className="mt-2 text-sm text-[var(--jj-text-secondary)] max-w-md mx-auto">
              {q || city ? "Try a different search." : "Be the first to submit a Kingdom-minded place."}
            </p>
            {user && (
              <Link to="/directory/submit" className="btn-primary !py-2 !px-5 text-sm mt-6 inline-block" data-testid="dir-empty-submit">
                Submit a listing
              </Link>
            )}
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {items.map((l) => <Card key={l.id} listing={l} />)}
          </div>
        )}
      </section>
    </div>
  );
}
